import { CARD_HEIGHT, CARD_WIDTH, Game } from '@phaser/scenes/Game';
import { CardObject } from '@phaser/objects/Card.object';
import { defaultText } from '@phaser/helpers/Text';
import { DEPTH } from '@phaser/data/depth';
import * as Shared from '@shared/lib';

type CardEffect = {
  image: Phaser.GameObjects.Image;
  text?: Phaser.GameObjects.Text;
  amount: number;
  code: number;
};

const MAX_EFFECTS_PER_ROW = 4;
const EFFECT_SIZE = 18;

export class EffectBehavior {
  private _scene: Game = Game.current;
  private _effects: CardEffect[] = [];
  private _timeouts: Timer[] = [];

  constructor(private _card: CardObject) {}

  public addEffect(effectCode: number, duration: number) {
    const data = Shared.effectData.find(a => a.id === effectCode);
    if (!data) return;

    const effect = this._effects.find(a => a.code === effectCode);

    const timer = setTimeout(() => {
      this._removeEffect(effectCode);
    }, duration);

    this._timeouts.push(timer);

    if (effect) {
      effect.amount++;

      if (effect.text) return effect.text.setText(effect.amount.toString());

      const textX = effect.image.x + EFFECT_SIZE / 2;
      const textY = effect.image.y + EFFECT_SIZE / 2;

      effect.text = this._scene.add.text(textX, textY, effect.amount.toString(), {
          ...defaultText,
          fontFamily: 'Kode Mono Variable',
          fontSize: '10px',
          strokeThickness: 0,
        })
        .setOrigin(0.5)
        .setDepth(DEPTH.CARD_EFFECT);
      this._card.container.add(effect.text);

      return;
    }

    const { x: xOffset, y: yOffset } = this._getPos(this._effects.length);

    const image = this._scene.add.image(xOffset, yOffset, 'sprites', `${data.key}Effect`)
      .setOrigin(0)
      .setDepth(DEPTH.CARD_EFFECT)
      .setScale(EFFECT_SIZE / 128);
    this._card.container.add(image);

    this._scene.tweens.add({
      targets: image,
      scale: { from: image.scale + 1, to: image.scale },
      alpha: { from: 0, to: 1 },
      duration: 600,
      ease: 'Power2',
    });

    this._effects.push({ code: effectCode, amount: 1, image });
  }

  public destroy() {
    this._effects.forEach(a => {
      a.image.destroy();
      a.text?.destroy();
    });
    this._effects = [];

    this._timeouts.forEach(a => clearTimeout(a));
    this._timeouts = [];
  }

  private _removeEffect(effectCode: number) {
    const effect = this._effects.find(a => a.code === effectCode);
    if (!effect) return;

    effect.amount--;

    if (effect.amount === 0) {
      effect.image.destroy();
      effect.text?.destroy();
      effect.text = undefined;
      this._effects = this._effects.filter(a => a.code !== effectCode);
    } else {
      if (effect.amount === 1) {
        effect.text?.destroy();
        effect.text = undefined;
      } else {
        effect.text?.setText(effect.amount.toString());
      }
    }

    this._effects.forEach((a, i) => {
      const { x: xOffset, y: yOffset } = this._getPos(i);
      a.image.setPosition(xOffset, yOffset);
      a.text?.setPosition(a.image.x + EFFECT_SIZE / 2, a.image.y + EFFECT_SIZE / 2);
    });
  }

  private _getPos(index: number) {
    const x = -CARD_WIDTH / 2 + (index % MAX_EFFECTS_PER_ROW) * EFFECT_SIZE;
    const y = -CARD_HEIGHT / 2 + 18 + Math.floor(index / MAX_EFFECTS_PER_ROW) * EFFECT_SIZE;
    return { x, y };
  }
}